import { Link } from "react-router-dom";
import { Facebook, Instagram, Youtube, Mail, Rss } from "lucide-react";
import logo from "@/assets/logo.webp";
import { useSiteSettings } from "@/hooks/useSiteSettings";
import SocialLinksPopover from "@/components/SocialLinksPopover";

const TikTokIcon = ({ className }: { className?: string }) => (
  <svg className={className} viewBox="0 0 24 24" fill="currentColor"><path d="M19.59 6.69a4.83 4.83 0 0 1-3.77-4.25V2h-3.45v13.67a2.89 2.89 0 0 1-2.88 2.5 2.89 2.89 0 0 1-2.89-2.89 2.89 2.89 0 0 1 2.89-2.89c.28 0 .54.04.79.1v-3.5a6.37 6.37 0 0 0-.79-.05A6.34 6.34 0 0 0 3.15 15a6.34 6.34 0 0 0 6.34 6.34 6.34 6.34 0 0 0 6.34-6.34V8.92a8.27 8.27 0 0 0 4.76 1.5V7a4.83 4.83 0 0 1-1-.31z" /></svg>
);

const PinterestIcon = ({ className }: { className?: string }) => (
  <svg className={className} viewBox="0 0 24 24" fill="currentColor"><path d="M12 0C5.373 0 0 5.373 0 12c0 5.084 3.163 9.426 7.627 11.174-.105-.949-.2-2.405.042-3.441.218-.937 1.407-5.965 1.407-5.965s-.359-.719-.359-1.782c0-1.668.967-2.914 2.171-2.914 1.023 0 1.518.769 1.518 1.69 0 1.029-.655 2.568-.994 3.995-.283 1.194.599 2.169 1.777 2.169 2.133 0 3.772-2.249 3.772-5.495 0-2.873-2.064-4.882-5.012-4.882-3.414 0-5.418 2.561-5.418 5.207 0 1.031.397 2.138.893 2.738a.36.36 0 0 1 .083.345l-.333 1.36c-.053.22-.174.267-.402.161-1.499-.698-2.436-2.889-2.436-4.649 0-3.785 2.75-7.262 7.929-7.262 4.163 0 7.398 2.967 7.398 6.931 0 4.136-2.607 7.464-6.227 7.464-1.216 0-2.359-.632-2.75-1.378l-.748 2.853c-.271 1.043-1.002 2.35-1.492 3.146C9.57 23.812 10.763 24 12 24c6.627 0 12-5.373 12-12S18.627 0 12 0z" /></svg>
);

const SnapchatIcon = ({ className }: { className?: string }) => (
  <svg className={className} viewBox="0 0 24 24" fill="currentColor"><path d="M12.206.793c.99 0 4.347.276 5.93 3.821.529 1.193.403 3.219.299 4.847l-.003.06c-.012.18-.022.345-.03.51.075.045.203.09.401.09.3-.016.659-.12.922-.214.12-.042.26-.076.382-.076.476 0 .873.323.873.738 0 .353-.249.685-.737.96-.547.31-1.335.545-1.706.614-.12.03-.203.06-.27.1-.09.06-.143.15-.165.27-.043.18.03.39.09.59.34 1.09.86 2.2 1.607 3.163.227.29.497.565.748.795.168.158.3.264.36.3.18.12.3.27.33.39.045.165-.03.348-.255.51-.33.225-.93.39-1.8.495-.12.015-.21.045-.255.105-.045.06-.09.18-.15.39l-.018.077c-.033.14-.065.286-.1.39-.06.165-.12.36-.51.36-.195 0-.42-.045-.69-.105a6.8 6.8 0 0 0-1.53-.195c-.27 0-.539.03-.81.09-.508.12-.947.408-1.43.72-.69.45-1.47.96-2.79.96s-2.1-.51-2.79-.96c-.484-.315-.923-.6-1.43-.72a4.58 4.58 0 0 0-.81-.09c-.57 0-1.11.105-1.53.195-.27.06-.494.105-.69.105-.39 0-.45-.195-.51-.36a8.5 8.5 0 0 0-.1-.39l-.018-.078c-.06-.21-.105-.33-.15-.39-.045-.06-.135-.09-.255-.105-.87-.105-1.47-.27-1.8-.495-.225-.165-.3-.345-.255-.51.03-.12.15-.27.33-.39.06-.03.195-.135.36-.3.252-.23.52-.503.748-.795.748-.963 1.267-2.073 1.607-3.162.06-.21.135-.42.09-.59-.023-.12-.075-.21-.165-.27-.067-.04-.15-.07-.27-.1-.37-.07-1.16-.304-1.706-.614-.49-.275-.738-.607-.738-.96 0-.415.397-.738.873-.738.122 0 .263.034.382.076.263.094.622.23.922.214.198 0 .326-.045.4-.09a7.5 7.5 0 0 1-.032-.51l-.002-.06c-.105-1.628-.23-3.654.299-4.847C7.86 1.069 11.216.793 12.206.793z" /></svg>
);

const defaultSocialLinks = [
  { icon: Facebook, url: "https://www.facebook.com/share/7QoPicexbWFPoFaE/?mibextid=hu50Ix", label: "Facebook", key: "social_facebook" },
  { icon: Instagram, url: "https://instagram.com/applesauceandadhd", label: "Instagram", key: "social_instagram" },
  { icon: TikTokIcon, url: "https://tiktok.com/@applesauceandadhd", label: "TikTok", key: "social_tiktok", custom: true },
  { icon: Youtube, url: "https://www.youtube.com/channel/UCxPtWxbojpMGbpQKXPthzuw", label: "YouTube", key: "social_youtube" },
  { icon: PinterestIcon, url: "https://www.pinterest.com/applesauceandadhd/", label: "Pinterest", key: "social_pinterest", custom: true },
  { icon: SnapchatIcon, url: "https://snapchat.com/t/eygNiay8", label: "Snapchat", key: "social_snapchat", custom: true },
];

// Export for use by other components
export const socialLinks = defaultSocialLinks;

const Footer = () => {
  const { data: settings } = useSiteSettings();

  const socialLinks = defaultSocialLinks.map((s) => ({
    ...s,
    url: (settings?.[s.key] as string) || s.url,
  }));

  const contactEmail = (settings?.contact_email as string) || "info@ofcourseitsgood.com";

  return (
    <footer className="bg-[hsl(var(--nav-bg))] border-t border-[hsl(var(--nav-foreground))]/10" role="contentinfo">
      <div className="container py-8 md:py-10">
        <div className="grid grid-cols-1 md:grid-cols-4 gap-8">
          {/* Brand */}
          <div className="md:col-span-1">
            <img src={logo} alt="Of Course It's Good" className="h-10 object-contain mb-3" width={142} height={40} />
            <p className="text-[hsl(var(--nav-foreground))]/60 text-sm leading-relaxed font-body">
              Recipes, lifestyle, and unapologetic flavor from Jessica Secrest — creator of <SocialLinksPopover />.
            </p>
            <a href={`mailto:${contactEmail}`} className="text-primary text-sm font-body mt-2 inline-flex items-center gap-1 hover:underline">
              <Mail className="h-3 w-3" /> {contactEmail}
            </a>
            <p className="font-handwritten text-primary text-xl mt-3">made with love (aggressively) ♡</p>
          </div>

          {/* Quick Links */}
          <div>
            <h2 className="font-heading uppercase tracking-wide text-sm text-[hsl(var(--nav-foreground))] mb-3">Explore</h2>
            <div className="flex flex-col gap-2">
              {[
                { label: "Recipes", to: "/recipes" },
                ...((settings as any)?.shop_visible !== false ? [{ label: "Shop My Favorites", to: "/shop" }] : []),
                { label: "Start Here", to: "/start-here" },
                { label: "Request a Recipe", to: "/recipe-request" },
                { label: "Blog", to: "/blog" },
                { label: "Cookbooks", to: "/cookbooks" },
                { label: "Events", to: "/events" },
                { label: "Install App", to: "/install" },
                { label: "RSS Feed", to: "/rss" },
              ].map((link) => (
                <Link key={link.to} to={link.to} className="text-sm text-[hsl(var(--nav-foreground))]/50 hover:text-primary transition-colors font-body">
                  {link.label}
                </Link>
              ))}
            </div>
          </div>

          {/* Connect */}
          <div>
            <h2 className="font-heading uppercase tracking-wide text-sm text-[hsl(var(--nav-foreground))] mb-3">Connect</h2>
            <div className="flex flex-col gap-2">
              {[
                { label: "About Me", to: "/about" },
                { label: "Press", to: "/press" },
                
                { label: "Subscribe", to: "/subscribe" },
              ].map((link) => (
                <Link key={link.to} to={link.to} className="text-sm text-[hsl(var(--nav-foreground))]/50 hover:text-primary transition-colors font-body">
                  {link.label}
                </Link>
              ))}
            </div>
          </div>

          {/* Legal */}
          <div>
            <h2 className="font-heading uppercase tracking-wide text-sm text-[hsl(var(--nav-foreground))] mb-3">Legal</h2>
            <div className="flex flex-col gap-2">
              <Link to="/privacy-policy" className="text-sm text-[hsl(var(--nav-foreground))]/50 hover:text-primary transition-colors font-body">
                Privacy Policy
              </Link>
              <Link to="/terms" className="text-sm text-[hsl(var(--nav-foreground))]/50 hover:text-primary transition-colors font-body">
                Terms & Conditions
              </Link>
              <Link to="/cookie-policy" className="text-sm text-[hsl(var(--nav-foreground))]/50 hover:text-primary transition-colors font-body">
                Cookie Policy
              </Link>
              <Link to="/affiliate-disclosure" className="text-sm text-[hsl(var(--nav-foreground))]/50 hover:text-primary transition-colors font-body">
                Affiliate Disclosure
              </Link>
              <Link to="/editorial-policy" className="text-sm text-[hsl(var(--nav-foreground))]/50 hover:text-primary transition-colors font-body">
                Editorial Policy
              </Link>
              <Link to="/accessibility" className="text-sm text-[hsl(var(--nav-foreground))]/50 hover:text-primary transition-colors font-body">
                Accessibility
              </Link>
              <Link to="/dmca" className="text-sm text-[hsl(var(--nav-foreground))]/50 hover:text-primary transition-colors font-body">
                DMCA Policy
              </Link>
            </div>
          </div>

          {/* Social */}
          <div>
            <h2 className="font-heading uppercase tracking-wide text-sm text-[hsl(var(--nav-foreground))] mb-3">Follow Along</h2>
            <div className="flex items-center gap-3">
              {socialLinks.map((s) => (
                <a
                  key={s.label}
                  href={s.url}
                  target="_blank"
                  rel="noopener noreferrer"
                  aria-label={s.label}
                  className="w-10 h-10 rounded-full bg-[hsl(var(--nav-foreground))]/10 flex items-center justify-center hover:bg-primary hover:text-primary-foreground transition-colors text-[hsl(var(--nav-foreground))]/60"
                >
                  <s.icon className="h-5 w-5" />
                </a>
              ))}
              <Link
                to="/rss"
                aria-label="RSS Feed"
                className="w-10 h-10 rounded-full bg-[hsl(var(--nav-foreground))]/10 flex items-center justify-center hover:bg-primary hover:text-primary-foreground transition-colors text-[hsl(var(--nav-foreground))]/60"
              >
                <Rss className="h-5 w-5" />
              </Link>
            </div>
          </div>
        </div>

        <div className="border-t border-[hsl(var(--nav-foreground))]/10 mt-10 pt-6 text-center space-y-2">
          <p className="text-xs text-[hsl(var(--nav-foreground))]/60 font-body">
            © {new Date().getFullYear()} Of Course It's Good. All rights reserved.
          </p>
          <p className="text-xs text-[hsl(var(--nav-foreground))]/60 font-body">
            <Link to="/privacy-policy" className="hover:text-primary transition-colors">Privacy</Link>
            {" · "}
            <Link to="/terms" className="hover:text-primary transition-colors">Terms</Link>
            {" · "}
            <Link to="/cookie-policy" className="hover:text-primary transition-colors">Cookies</Link>
            {" · "}
            <Link to="/affiliate-disclosure" className="hover:text-primary transition-colors">Affiliate</Link>
            {" · "}
            <Link to="/accessibility" className="hover:text-primary transition-colors">Accessibility</Link>
            {" · "}
            <Link to="/dmca" className="hover:text-primary transition-colors">DMCA</Link>
          </p>
          <p className="text-xs text-[hsl(var(--nav-foreground))]/50 font-body">
            Made by{" "}
            <a href="https://itsmannymichael.com" target="_blank" rel="noopener noreferrer" className="hover:text-primary transition-colors">
              Manny Michael
            </a>
          </p>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
